import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
} from '../../api/notifications';

const TABS = [
  { key: 'all',       label: 'All Notifications' },
  { key: 'expiry',    label: 'Contract Expiry' },
  { key: 'quotation', label: 'Quotations' },
  { key: 'service',   label: 'Service Updates' },
  { key: 'visitor',   label: 'Visitor Alerts' },
];

const COLOR_MAP = {
  expiry:    { border: '#EF4444', bg: 'rgba(239,68,68,0.05)' },
  quotation: { border: '#8B5CF6', bg: 'rgba(139,92,246,0.05)' },
  service:   { border: '#10B981', bg: 'rgba(16,185,129,0.05)' },
  visitor:   { border: '#3B82F6', bg: 'rgba(59,130,246,0.05)' },
  general:   { border: '#666',    bg: 'rgba(100,100,100,0.05)' },
};

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'Just now';
  if (mins < 60)  return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotifications(activeTab === 'all' ? '' : activeTab);
      setNotifications(res.data || []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = async (id, source) => {
    if (source === 'derived') {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      return;
    }
    await markAsRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const handleDelete = async (id, source) => {
    if (source === 'derived') {
      setNotifications(prev => prev.filter(n => n.id !== id));
      return;
    }
    await deleteNotification(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <AppLayout title="Notifications" breadcrumbs="Notifications">
      {/* Sub Nav */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{ background: 'white', borderBottom: '1px solid #E5E5E0', padding: '0 40px', gap: '24px' }}
      >
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer flex items-center gap-2"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: activeTab === tab.key ? '3px solid #043959' : '3px solid transparent',
            }}
          >
            {tab.label}
            {tab.key === 'all' && unreadCount > 0 && (
              <span style={{ background: '#EF4444', color: 'white', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: '32px 0' }}>
        <div className="bg-white rounded-2xl" style={{ border: '1px solid #E5E5E0' }}>
          {/* Header */}
          <div className="flex items-center justify-between" style={{ padding: '20px 24px', borderBottom: '1px solid #E5E5E0' }}>
            <h3 className="text-base font-semibold" style={{ color: '#1A1A1A' }}>
              Recent Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-sm font-normal" style={{ color: '#999' }}>
                  {unreadCount} unread
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm font-medium rounded-lg cursor-pointer transition-colors"
                style={{ padding: '8px 16px', border: '1px solid #E5E5E0', background: 'white', color: '#043959', fontFamily: "'Lexend', sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                Mark All as Read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ padding: '16px' }}>
            {loading ? (
              <div className="text-center text-sm py-10" style={{ color: '#999' }}>Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-4xl mb-3">🔔</div>
                <p className="text-sm" style={{ color: '#999' }}>No notifications in this category</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {notifications.map(n => {
                  const colors = COLOR_MAP[n.category] || COLOR_MAP.general;
                  return (
                    <div
                      key={n.id}
                      className="flex gap-3 items-start rounded-xl transition-all"
                      style={{
                        padding: '16px',
                        borderLeft: `3px solid ${colors.border}`,
                        background: n.is_read ? 'white' : colors.bg,
                        border: `1px solid ${n.is_read ? '#F0F0EB' : colors.border}`,
                        borderLeftWidth: 3,
                        opacity: n.is_read ? 0.75 : 1,
                      }}
                    >
                      {/* Icon */}
                      <div
                        className="flex items-center justify-center flex-shrink-0 text-base"
                        style={{ width: 36, height: 36, borderRadius: '50%', background: n.color, color: 'white' }}
                      >
                        {n.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-semibold" style={{ color: '#1A1A1A', marginBottom: 4 }}>
                            {n.title}
                            {!n.is_read && (
                              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#EF4444', marginLeft: 6, verticalAlign: 'middle' }} />
                            )}
                          </h4>
                        </div>
                        <p className="text-sm" style={{ color: '#666', marginBottom: 4, lineHeight: 1.5 }}>{n.message}</p>
                        <span className="text-xs" style={{ color: '#999' }}>{timeAgo(n.created_at)}</span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!n.is_read && (
                          <button
                            onClick={() => handleMarkRead(n.id, n.source)}
                            className="text-xs font-medium rounded-lg cursor-pointer border-0 transition-colors"
                            style={{ padding: '5px 10px', background: 'rgba(4,57,89,0.08)', color: '#043959', fontFamily: "'Lexend', sans-serif" }}
                            title="Mark as read"
                          >
                            ✓ Read
                          </button>
                        )}
                        {n.source !== 'derived' && (
                          <button
                            onClick={() => handleDelete(n.id, n.source)}
                            className="text-xs rounded-lg cursor-pointer border-0 transition-colors"
                            style={{ padding: '5px 8px', background: 'rgba(239,68,68,0.08)', color: '#EF4444', fontFamily: "'Lexend', sans-serif" }}
                            title="Delete"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
